import { useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import type { DictionaryEntry } from '../services/api';

interface DictionaryTableProps {
  data: DictionaryEntry[];
  onEdit?: (entry: DictionaryEntry) => void;
  onDelete?: (id: number) => void;
}

export default function DictionaryTable({ data, onEdit, onDelete }: DictionaryTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const columns = useMemo<ColumnDef<DictionaryEntry>[]>(
    () => [
      {
        accessorKey: 'group_name',
        header: 'Group',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'field_name',
        header: 'Field Name',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'app_label',
        header: 'App Label',
        cell: (info) => info.getValue() || '-',
      },
      {
        accessorKey: 'sql_data_type',
        header: 'Data Type',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'is_required',
        header: 'Required',
        cell: (info) => ((info.getValue() as number) === 1 ? 'Yes' : 'No'),
      },
      {
        accessorKey: 'is_ready',
        header: 'Ready',
        cell: (info) => ((info.getValue() as number) === 1 ? 'Yes' : 'No'),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const entry = info.row.original;
          return (
            <div className="flex space-x-2">
              {onEdit && (
                <button
                  onClick={() => onEdit(entry)}
                  className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                >
                  Edit
                </button>
              )}
              {onDelete && entry.id && (
                <button
                  onClick={() => onDelete(entry.id!)}
                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  Delete
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [onEdit, onDelete]
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="card">
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="input max-w-md"
        />
      </div>
      <div className="overflow-x-auto">
        <table className="table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="cursor-pointer select-none"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className="flex items-center space-x-1">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {{
                        asc: ' ↑',
                        desc: ' ↓',
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {table.getRowModel().rows.length === 0 && (
        <div className="text-center py-8 text-gray-500">No entries found</div>
      )}
    </div>
  );
}

